/**
 * The relationship between a bundle's price and its parts.
 *
 * `bundles.price` and `courses.price` are typed in independently and nothing
 * has ever compared them, so a pack's discount drifts silently: add a course
 * and the parts total moves while the pack price sits still. This computes the
 * comparison so the two places that matter can show it — the admin editor,
 * where drift is created, and the learner's card, where the discount is
 * supposed to be doing sales work.
 *
 * Deliberately a mirror, not a rule: the bundle keeps its absolute price so a
 * pack can be R120 rather than whatever 23% off happens to produce. Nothing
 * here blocks a save.
 */

export type BundlePriceVerdict =
  /** The pack is cheaper than its parts — the normal, intended case. */
  | 'saving'
  /** Priced exactly at the sum of its parts: no reason to prefer the pack. */
  | 'no-saving'
  /** The pack costs MORE than buying the courses separately. */
  | 'costs-more'
  /** Members are free or there are none, so there's nothing to compare against. */
  | 'no-anchor';

export interface BundlePriceComparison {
  courseCount: number;
  partsTotal: number;
  bundlePrice: number;
  /** `partsTotal - bundlePrice`; negative when the pack is the worse deal. */
  saving: number;
  /** Saving as a whole percent of the parts total; 0 when there's no anchor. */
  savingPercent: number;
  verdict: BundlePriceVerdict;
}

export function compareBundlePrice(
  bundlePrice: number,
  memberPrices: number[],
): BundlePriceComparison {
  const courseCount = memberPrices.length;
  // Guard the inputs: `price` arrives from a free-typed form field and from a
  // numeric DB column that can be null, and one NaN would poison the total.
  const partsTotal = memberPrices.reduce(
    (sum, p) => sum + (Number.isFinite(p) ? Math.max(p, 0) : 0),
    0,
  );
  const price = Number.isFinite(bundlePrice) ? Math.max(bundlePrice, 0) : 0;
  const saving = partsTotal - price;

  if (courseCount === 0 || partsTotal <= 0) {
    return {
      courseCount,
      partsTotal,
      bundlePrice: price,
      saving: 0,
      savingPercent: 0,
      verdict: 'no-anchor',
    };
  }

  return {
    courseCount,
    partsTotal,
    bundlePrice: price,
    saving,
    savingPercent: Math.round((saving / partsTotal) * 100),
    verdict: saving > 0 ? 'saving' : saving === 0 ? 'no-saving' : 'costs-more',
  };
}
